import React, { useState } from "react";
import {
  Card,
  Typography,
  Table,
  Space,
  DatePicker,
  Button,
  Statistic,
  Row,
  Col,
  Tag,
  Input,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadPurchaseOrderReport,
  useReportsServiceGetPurchaseOrderReport,
} from "api/queries";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const statusColors: Record<string, string> = {
  DRAFT: "default",
  PARTIALLY_PAID: "orange",
  PAID: "green",
  CANCELLED: "red",
};

const deliveryStatusColors: Record<string, string> = {
  PROCESSING: "blue",
  DELIVERED: "green",
  CANCELLED: "red",
};

const PurchaseOrderSummary: React.FC = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("Custom");
  const [search, setSearch] = useState("");
  const { data, isLoading, refetch } = useReportsServiceGetPurchaseOrderReport({
    limit: 10,
    offset: 0,
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    search,
  });
  const { mutate: downloadPurchaseOrderSummaryReport, isPending } =
    useReportsServiceDownloadPurchaseOrderReport();

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
    refetch();
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };

  const handleDownload = () => {
    downloadPurchaseOrderSummaryReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "purchase_order_summary.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  type KPIs = {
    totalOrders?: number;
    totalVendors?: number;
    totalValue?: number;
    totalEstimatedRevenue?: number;
  };
  const kpis: KPIs = data?.kpis || {};
  const tableData = (data?.items || []).map((item: any) => ({
    ...item,
    key: item.purchaseOrderId,
  }));

  const columns = [
    {
      title: "Order #",
      dataIndex: "purchaseOrderNumber",
      key: "purchaseOrderNumber",
    },
    { title: "Vendor", dataIndex: "vendorName", key: "vendorName" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={statusColors[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Delivery Status",
      dataIndex: "deliveryStatus",
      key: "deliveryStatus",
      render: (deliveryStatus: string) =>
        deliveryStatus ? (
          <Tag color={deliveryStatusColors[deliveryStatus] || "default"}>
            {deliveryStatus}
          </Tag>
        ) : (
          <Tag>--</Tag>
        ),
    },
    {
      title: "Creation Date",
      dataIndex: "purchaseOrderDate",
      key: "purchaseOrderDate",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Total Value",
      dataIndex: "totalValue",
      key: "totalValue",
      render: (value: number) => `${value?.toLocaleString?.() ?? value}`,
    },
    {
      title: "Estimated Revenue",
      dataIndex: "estimatedRevenue",
      key: "estimatedRevenue",
      render: (value: number) => `${value?.toLocaleString?.() ?? value}`,
    },
    {
      title: "Products",
      dataIndex: "productNames",
      key: "productNames",
      render: (products: string[]) =>
        products?.length ? products.join(", ") : "-",
    },
  ];

  // Debounce search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    refetch();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Purchase Order Summary</Title>
          <Space>
            <Input.Search
              allowClear
              placeholder="Search purchase orders..."
              style={{ width: 260 }}
              value={search}
              onChange={handleSearchChange}
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title="Total Orders" value={kpis?.totalOrders ?? 0} />
          </Col>
          <Col span={6}>
            <Statistic title="Total Vendors" value={kpis?.totalVendors ?? 0} />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Value"
              value={kpis?.totalValue ?? 0}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Estimated Revenue"
              value={kpis?.totalEstimatedRevenue ?? 0}
            />
          </Col>
        </Row>
        <Card>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={isLoading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: "max-content" }}
          />
        </Card>
      </Space>
    </motion.div>
  );
};

export default PurchaseOrderSummary;
